/** Shared read-only Teacher reporting composition; analytics stay with each screen. */
import React, { useId } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';
import StudentAvatarView from '../shared/StudentAvatarView';
import { unifiedUI as ui } from './unified';

const focus = { '&:focus-visible, &.Mui-focusVisible': { outline: '3px solid #5336AA', outlineOffset: 2 } };
const meta = { fontSize: 12, lineHeight: 1.5, color: ui.secondary };
const titleStyle = { fontSize: 14, lineHeight: 1.45, fontWeight: 600, color: ui.ink, overflowWrap: 'anywhere' };

export { UnifiedMetricCard as TeacherMetricCard, UnifiedSectionHeader as TeacherSectionHeader } from './UnifiedComponents';

export function TeacherStudentIdentity({ student, metadata, detail = false }) {
  return <Box data-teacher-identity sx={{ display: 'grid', gridTemplateColumns: `${detail ? 64 : 40}px minmax(0,1fr)`, gap: detail ? 1.5 : 1.25, alignItems: 'center', minWidth: 0 }}>
    <StudentAvatarView studentId={student.id} avatarId={student.avatarId} starterEmoji={student.starterEmoji} name={student.name} size={detail ? 64 : 40} />
    <Box sx={{ minWidth: 0 }}>
      <Typography component={detail ? 'h2' : 'span'} sx={{ ...titleStyle, display: 'block', fontSize: detail ? 20 : 14 }}>{student.name}</Typography>
      {metadata && <Typography component="span" sx={{ ...meta, display: 'block', mt: 0.25 }}>{metadata}</Typography>}
    </Box>
  </Box>;
}

function TeacherValue({ value, label, numeric, id }) {
  return <Box id={id} data-teacher-value sx={{ width: 64, textAlign: 'right', alignSelf: 'center', fontVariantNumeric: 'tabular-nums' }}>
    <Typography component="span" sx={{ display: 'block', fontSize: numeric ? 21 : 16, fontWeight: 700, lineHeight: 1.3, letterSpacing: '-0.025em', color: numeric ? ui.purple : ui.secondary }}>{value}</Typography>
    {label && <Typography component="span" sx={{ ...meta, display: 'block', fontSize: 11 }}>{label}</Typography>}
  </Box>;
}

export function TeacherStudentResultRow({ student, value, metadata, summary, onClick, actionLabel }) {
  const numeric = Number.isFinite(value);
  const resultId = useId();
  return <Box component={onClick ? Button : 'div'} type={onClick ? 'button' : undefined} onClick={onClick}
    role={onClick ? undefined : 'group'} aria-label={onClick ? actionLabel : student.name}
    aria-describedby={onClick ? [numeric && `${resultId}-value`, summary && `${resultId}-summary`].filter(Boolean).join(' ') || undefined : undefined}
    color={onClick ? 'inherit' : undefined} data-teacher-student-row
    sx={{ ...focus, width: '100%', minWidth: 0, minHeight: 64, px: 0, py: 1.25, m: 0,
      display: 'grid', gridTemplateColumns: `minmax(0,1fr)${numeric ? ' 64px' : ''}${onClick ? ' 16px' : ''}`, columnGap: 1,
      alignItems: 'center', textAlign: 'left', textTransform: 'none', borderRadius: 0, borderBottom: `1px solid ${ui.border}`,
      '&:last-child': { borderBottom: 0 }, '&:hover': onClick ? { bgcolor: ui.surfaceSubtle } : {} }}>
    <TeacherStudentIdentity student={student} metadata={metadata} />
    {numeric && <TeacherValue id={`${resultId}-value`} value={value.toLocaleString('tr-TR', { maximumFractionDigits: 1 })} label="/ 100" numeric />}
    {onClick && <ChevronRightIcon sx={{ width: 16, color: ui.secondary }} aria-hidden="true" />}
    {summary && <Typography id={`${resultId}-summary`} component="span" data-teacher-category-summary sx={{ ...meta, gridColumn: '1 / -1', pl: '50px', mt: 0.75,
      overflowWrap: 'anywhere' }}>{summary}</Typography>}
  </Box>;
}

export function TeacherAssessmentHistoryRow({ title, dateLabel, metadata, value, label = 'ortalama', summary, onClick, actionLabel }) {
  const numeric = Number.isFinite(value);
  const resultId = useId();
  return <Box component={onClick ? Button : 'div'} type={onClick ? 'button' : undefined} onClick={onClick}
    role={onClick ? undefined : 'group'} aria-label={onClick ? actionLabel : title}
    aria-describedby={onClick ? [numeric && `${resultId}-value`, summary && `${resultId}-summary`].filter(Boolean).join(' ') || undefined : undefined}
    color={onClick ? 'inherit' : undefined} data-teacher-history-row
    sx={{ ...focus, display: 'grid', gridTemplateColumns: `minmax(0,1fr)${numeric ? ' 64px' : ''}${onClick ? ' 16px' : ''}`, columnGap: 1.25,
      alignItems: 'center', minWidth: 0, width: '100%', minHeight: 64, textAlign: 'left', textTransform: 'none', borderRadius: 0,
      px: 0, py: 1.25, borderBottom: `1px solid ${ui.border}`, '&:last-child': { borderBottom: 0 },
      '&:hover': onClick ? { bgcolor: ui.surfaceSubtle } : {} }}>
    <Box sx={{ minWidth: 0 }}>
      <Typography component="span" data-testid="classpulse-assessment-title" sx={{ ...titleStyle, display: 'block' }}>{title}</Typography>
      <Typography component="span" sx={{ ...meta, display: 'block', mt: 0.25 }}>{dateLabel}{metadata ? ` · ${metadata}` : ''}</Typography>
    </Box>
    {numeric && <TeacherValue id={`${resultId}-value`} numeric value={value.toLocaleString('tr-TR', { maximumFractionDigits: 1 })} label={label} />}
    {onClick && <ChevronRightIcon sx={{ width: 16, color: ui.secondary }} aria-hidden="true" />}
    {summary && <Typography id={`${resultId}-summary`} component="span" data-teacher-category-summary sx={{ ...meta, gridColumn: '1 / -1', mt: 0.75, color: ui.purple }}>{summary}</Typography>}
  </Box>;
}

export function TeacherDrawerShell({ open, onClose, label, children }) {
  return <Drawer anchor="right" open={open} onClose={onClose} ModalProps={{ keepMounted: false }}
    PaperProps={{ role: 'dialog', 'aria-modal': true, 'aria-label': label, sx: { width: { xs: '100%', sm: '85vw', md: 'min(760px, 64vw)', xl: '800px' },
      maxWidth: '100%', bgcolor: ui.surface, fontSynthesis: 'none', borderTopLeftRadius: { xs: 0, sm: 14 },
      borderBottomLeftRadius: { xs: 0, sm: 14 }, boxShadow: '0 12px 40px rgba(28,24,75,.18)' } }}>
    {children}
  </Drawer>;
}

export function TeacherDrawerNav({ onClose, back, label, context, children }) {
  return <Box data-teacher-drawer-nav sx={{ display: 'grid', gridTemplateColumns: '44px minmax(0,1fr) auto', gap: 1, alignItems: 'center',
    mb: 2, pb: 1, borderBottom: `1px solid ${ui.border}`, minHeight: 56 }}>
    <IconButton onClick={onClose} aria-label={label} sx={{ ...focus, width: 44, height: 44 }}>{back ? <ArrowBackIcon /> : <CloseIcon />}</IconButton>
    <Typography sx={{ ...meta, textAlign: 'center' }}>{context}</Typography>
    {children || <Box sx={{ width: 44 }} />}
  </Box>;
}

